import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import { debounceTime, map, startWith, switchMap, tap } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { UntypedFormControl, Validators } from "@angular/forms";
import { MatOptionSelectionChange } from "@angular/material/core";
import { LoadingDrivebService } from "@shared/components/loading-driveb/loading-driveb.service";
import {
  GenericAutocompleteService,
  IGenericAutocompleteService
} from "./generic-autocomplete.service";

@Component({
    selector: "app-driveb-generic-autocomplete",
    templateUrl: "./generic-autocomplete.component.html",
    styleUrls: ["./generic-autocomplete.component.scss"],
    standalone: false
})
export class GenericAutocompleteComponent implements OnInit, OnChanges {
  loading = false;
  control = new UntypedFormControl("");
  dataContent$: Observable<FormattedData[]>;

  @Input() title = "";
  @Input() key = "";
  @Input() otherParam = "";
  @Input() method: keyof IGenericAutocompleteService;
  @Input() sendDataOnBlur = false;
  @Input() required: boolean;
  @Input() set disabled(value: boolean) {
    if (value) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  @Output() onChange = new EventEmitter<FormattedData>();

  constructor(
    private loadingDrivebService: LoadingDrivebService,
    private genericAutocompleteService: GenericAutocompleteService
  ) {}

  getDataFromLocalStorage() {
    const localStorageData = localStorage.getItem(this.key);
    if (localStorageData != null && localStorageData != "") {
      const data = JSON.parse(localStorageData);
      this.control.setValue(data);
    }
  }

  ngOnInit(): void {
    this.getDataFromLocalStorage();

    this.dataContent$ = this.control.valueChanges.pipe(
      startWith(""),
      debounceTime(200),
      tap(
        () => (
          (this.loading = true), this.loadingDrivebService.setAutoMode(false)
        )
      ),
      switchMap((value: string) => {
        if (value) {
          return this.genericAutocompleteService[this.method](
            value,
            this.otherParam
          );
        } else {
          return of([]) as Observable<FormattedData[]>;
        }
      }),
      map((result) => {
        this.loading = false;
        this.loadingDrivebService.setAutoMode(true);
        return result;
      })
    );
  }

  ngOnChanges(): void {
    if (this.required && !this.disabled) {
      this.control.setValidators(Validators.required);
    }

    this.control.updateValueAndValidity();
  }

  displayFn(data: FormattedData): string {
    if (data) {
      return data.label;
    }

    return "";
  }

  onBlur(): void {
    if (this.sendDataOnBlur && typeof this.control.value === "string") {
      this.onChange.emit({
        label: this.control.value,
        value: null
      });
    }
  }

  handleChange(event: MatOptionSelectionChange, data: FormattedData) {
    if (event.isUserInput) {
      this.onChange.emit(data);
    }
  }

  clean(): void {
    this.control.reset();
  }
}

export type FormattedData = {
  value: any;
  label: string;
  [key: string]: any;
};
